import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AuthContext from '../components/shared/AuthContext';
import { useLearningRealtime } from '../realtime/LearningRealtimeProvider';
import {
  canAccessTeamWorkspace,
  canCreateTeamOrganization,
  canUseFeature,
  normalizePlan,
} from '../config/planFeatures';
import { subscribeRealtimeInvalidate } from '../realtime/realtimeBus';

const UserPlanContext = createContext(null);

export function UserPlanProvider({ children }) {
  const { apiClient, isAuthenticated } = useContext(AuthContext);
  const live = useLearningRealtime();
  const [fetchedPlan, setFetchedPlan] = useState(null);
  const [planReady, setPlanReady] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setFetchedPlan(null);
      setPlanReady(true);
      return undefined;
    }

    let cancelled = false;

    const loadPlan = async () => {
      setPlanReady(false);
      try {
        const res = await apiClient.get('/api/users/me/usage');
        if (!cancelled) {
          const effective = res.data?.effectivePlan ?? res.data?.plan ?? 'FREE';
          setFetchedPlan(effective);
        }
      } catch {
        if (!cancelled) setFetchedPlan('FREE');
      } finally {
        if (!cancelled) setPlanReady(true);
      }
    };

    loadPlan();

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadPlan();
    };
    document.addEventListener('visibilitychange', onVisible);

    const unsubInvalidate = subscribeRealtimeInvalidate((detail) => {
      if (detail?.reason === 'entitlements' || detail?.reason === 'billing') {
        loadPlan();
      }
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      unsubInvalidate();
    };
  }, [apiClient, isAuthenticated, live?.usage?.plan]);

  const livePlan = live?.usage?.effectivePlan ?? live?.usage?.plan;
  const plan = normalizePlan(livePlan ?? fetchedPlan);
  const usage = live?.usage ?? (fetchedPlan != null ? { plan: fetchedPlan } : null);
  const accessContext = useMemo(() => ({ plan }), [plan]);

  const value = useMemo(
    () => ({
      plan,
      usage,
      planReady,
      accessContext,
      canUseFeature: (feature) => canUseFeature(feature, accessContext),
      canAccessTeams: canAccessTeamWorkspace(accessContext),
      canCreateTeam: canCreateTeamOrganization(accessContext),
    }),
    [plan, usage, planReady, accessContext]
  );

  return <UserPlanContext.Provider value={value}>{children}</UserPlanContext.Provider>;
}

export function useUserPlan() {
  const ctx = useContext(UserPlanContext);
  if (!ctx) {
    throw new Error('useUserPlan must be used within UserPlanProvider');
  }
  return ctx;
}

export default UserPlanContext;
